// src/domain/models/Profile.ts
import type { Role } from '../constants/roles'

export interface ProfileRef {
  uuid: string | null
  name: string | null
}

export interface ProfileAddress {
  line_1: string | null
  line_2: string | null
  city: string | null
  zip: string | null
  state: string | null
  country: string | null
}

export interface ProfileLocation {
  uuid: string | null
  name: string | null
  address: ProfileAddress | null
  inactive: boolean
  last_update: number | null
}

export interface ProfileGroup {
  _id: string | null
  name: string | null
}

export interface ProfileBranch {
  uuid: string | null
  name: string | null
  banner: string | null
  location: ProfileLocation | null
  organization: ProfileRef | null
  last_update: number | null
}

export interface ProfileData {
  _id: string | null
  id: string | null
  name: string | null
  username: string | null
  userid: string | null
  email: string | null
  alias: string | null
  type: string | null
  companyName: string | null
  accessType: string | null
  default_role: string | null
  roles: Role[]
  default_group: string | null
  groups: Array<string | ProfileGroup>
  ticketScopes: string[]
  banners: string[]
  branches: ProfileBranch[]
  location: ProfileLocation | null
  organization: ProfileRef | null
  organizations: ProfileRef[]
  admin_organization: ProfileRef | null
  inactive: boolean
  last_update: number | null
  homeAccountId: string | null
  localAccountId: string | null
  lang: string | null
  accessGroups: string[]
}

function toStringOrNull (value: any): string | null {
  if (value === undefined || value === null) {
    return null
  }

  return String(value)
}

function toNumberOrNull (value: any): number | null {
  if (value === undefined || value === null || value === '') {
    return null
  }

  const num = Number(value)

  if (Number.isNaN(num)) {
    return null
  }

  return num
}

function toStringArray (value: any): string[] {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item) => toStringOrNull(item))
    .filter((item): item is string => Boolean(item))
}

function toRoleArray (value: any): Role[] {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item: any) => toStringOrNull(item))
    .filter((item: string | null): item is Role => Boolean(item))
}

function normalizeRef (value: any): ProfileRef | null {
  if (!value || typeof value !== 'object') {
    return null
  }

  return {
    uuid: toStringOrNull(value.uuid),
    name: toStringOrNull(value.name)
  }
}

function normalizeAddress (value: any): ProfileAddress | null {
  if (!value || typeof value !== 'object') {
    return null
  }

  return {
    line_1: toStringOrNull(value.line_1),
    line_2: toStringOrNull(value.line_2),
    city: toStringOrNull(value.city),
    zip: toStringOrNull(value.zip),
    state: toStringOrNull(value.state),
    country: toStringOrNull(value.country)
  }
}

function normalizeLocation (value: any): ProfileLocation | null {
  if (!value || typeof value !== 'object') {
    return null
  }

  return {
    uuid: toStringOrNull(value.uuid),
    name: toStringOrNull(value.name),
    address: normalizeAddress(value.address),
    inactive: Boolean(value.inactive),
    last_update: toNumberOrNull(value.last_update)
  }
}

function normalizeGroup (value: any): string | ProfileGroup | null {
  if (typeof value === 'string') {
    return value
  }

  if (!value || typeof value !== 'object') {
    return null
  }

  return {
    _id: toStringOrNull(value._id),
    name: toStringOrNull(value.name)
  }
}

function normalizeBranch (value: any): ProfileBranch | null {
  if (!value || typeof value !== 'object') {
    return null
  }

  return {
    uuid: toStringOrNull(value.uuid),
    name: toStringOrNull(value.name),
    banner: toStringOrNull(value.banner),
    location: normalizeLocation(value.location),
    organization: normalizeRef(value.organization),
    last_update: toNumberOrNull(value.last_update)
  }
}

export class Profile {
  _id: string | null
  id: string | null
  name: string | null
  username: string | null
  userid: string | null
  email: string | null
  alias: string | null
  type: string | null
  companyName: string | null
  accessType: string | null
  default_role: string | null
  roles: Role[]
  default_group: string | null
  groups: Array<string | ProfileGroup>
  ticketScopes: string[]
  banners: string[]
  branches: ProfileBranch[]
  location: ProfileLocation | null
  organization: ProfileRef | null
  organizations: ProfileRef[]
  admin_organization: ProfileRef | null
  inactive: boolean
  last_update: number | null
  homeAccountId: string | null
  localAccountId: string | null
  lang: string | null
  accessGroups: string[]

  constructor (data: any = {}) {
    this._id = toStringOrNull(data._id)
    this.id = toStringOrNull(data.id)
    this.name = toStringOrNull(data.name)
    this.username = toStringOrNull(data.username)
    this.userid = toStringOrNull(data.userid)
    this.email = toStringOrNull(data.email)
    this.alias = toStringOrNull(data.alias)
    this.type = toStringOrNull(data.type)
    this.companyName = toStringOrNull(data.companyName)
    this.accessType = toStringOrNull(data.accessType)
    this.default_role = toStringOrNull(data.default_role)
    this.roles = toRoleArray(data.roles)
    this.default_group = toStringOrNull(data.default_group)
    this.groups = Array.isArray(data.groups)
      ? data.groups
        .map((item: any) => normalizeGroup(item))
        .filter((item: string | ProfileGroup | null): item is string | ProfileGroup => Boolean(item))
      : []
    this.ticketScopes = toStringArray(data.ticketScopes)
    this.banners = toStringArray(data.banners)
    this.branches = Array.isArray(data.branches)
      ? data.branches
        .map((item: any) => normalizeBranch(item))
        .filter((item: ProfileBranch | null): item is ProfileBranch => Boolean(item))
      : []
    this.location = normalizeLocation(data.location)
    this.organization = normalizeRef(data.organization)
    this.organizations = Array.isArray(data.organizations)
      ? data.organizations
        .map((item: any) => normalizeRef(item))
        .filter((item: ProfileRef | null): item is ProfileRef => Boolean(item))
      : []
    this.admin_organization = normalizeRef(data.admin_organization)
    this.inactive = Boolean(data.inactive)
    this.last_update = toNumberOrNull(data.last_update)
    this.homeAccountId = toStringOrNull(data.homeAccountId)
    this.localAccountId = toStringOrNull(data.localAccountId)
    this.lang = toStringOrNull(data.lang)
    this.accessGroups = toStringArray(data.accessGroups)
  }

  static from (data: any): Profile {
    return new Profile(data)
  }

  static fromMany (list: any): Profile[] {
    if (!Array.isArray(list)) {
      return []
    }

    return list.map((item) => new Profile(item))
  }

  get uuid (): string | null {
    return this._id || this.id
  }

  get displayName (): string {
    return this.name || this.username || this.email || 'Unknown profile'
  }

  get initials (): string {
    if (!this.name) {
      return ''
    }

    const first = this.name.split(' ')[0].charAt(0)
    const last = this.name.split(' ')[1]?.charAt(0) || ''
    return `${first !== '(' ? first : ''}${last}`
  }

  get isCpu (): boolean {
    return this.accessType === 'CPU_ACCESS'
  }

  get isClient (): boolean {
    return this.accessType === 'CLIENT_ACCESS'
  }

  get isPartner (): boolean {
    return this.accessType === 'PARTNER_ACCESS'
  }

  get isActive (): boolean {
    return !this.inactive
  }

  get groupIds (): string[] {
    return this.groups
      .map((group) => {
        if (typeof group === 'string') {
          return group
        }

        return group._id
      })
      .filter((value): value is string => Boolean(value))
  }

  get groupNames (): string[] {
    return this.groups
      .map((group) => {
        if (typeof group === 'string') {
          return null
        }

        return group.name
      })
      .filter((value): value is string => Boolean(value))
  }

  get roleNames (): Role[] {
    return [...this.roles]
  }

  hasRole (needed: Role | Role[]): boolean {
    if (!this.roles) {
      return false
    }

    const neededRoles = Array.isArray(needed) ? needed : [needed]
    const profileRoles = this.roles.filter((code) => neededRoles.includes(code))
    return !!profileRoles.length
  }

  hasScope (scope: string | null | undefined): boolean {
    if (!scope) {
      return false
    }

    return this.ticketScopes.includes(scope)
  }

  toJSON (): ProfileData {
    return {
      _id: this._id,
      id: this.id,
      name: this.name,
      username: this.username,
      userid: this.userid,
      email: this.email,
      alias: this.alias,
      type: this.type,
      companyName: this.companyName,
      accessType: this.accessType,
      default_role: this.default_role,
      roles: [...this.roles],
      default_group: this.default_group,
      groups: this.groups.map((group) => {
        if (typeof group === 'string') {
          return group
        }

        return {
          _id: group._id,
          name: group.name
        }
      }),
      ticketScopes: [...this.ticketScopes],
      banners: [...this.banners],
      branches: this.branches.map((branch) => ({
        uuid: branch.uuid,
        name: branch.name,
        banner: branch.banner,
        location: branch.location
          ? {
              uuid: branch.location.uuid,
              name: branch.location.name,
              address: branch.location.address
                ? {
                    line_1: branch.location.address.line_1,
                    line_2: branch.location.address.line_2,
                    city: branch.location.address.city,
                    zip: branch.location.address.zip,
                    state: branch.location.address.state,
                    country: branch.location.address.country
                  }
                : null,
              inactive: branch.location.inactive,
              last_update: branch.location.last_update
            }
          : null,
        organization: branch.organization
          ? {
              uuid: branch.organization.uuid,
              name: branch.organization.name
            }
          : null,
        last_update: branch.last_update
      })),
      location: this.location
        ? {
            uuid: this.location.uuid,
            name: this.location.name,
            address: this.location.address
              ? {
                  line_1: this.location.address.line_1,
                  line_2: this.location.address.line_2,
                  city: this.location.address.city,
                  zip: this.location.address.zip,
                  state: this.location.address.state,
                  country: this.location.address.country
                }
              : null,
            inactive: this.location.inactive,
            last_update: this.location.last_update
          }
        : null,
      organization: this.organization
        ? {
            uuid: this.organization.uuid,
            name: this.organization.name
          }
        : null,
      organizations: this.organizations.map((item) => ({
        uuid: item.uuid,
        name: item.name
      })),
      admin_organization: this.admin_organization
        ? {
            uuid: this.admin_organization.uuid,
            name: this.admin_organization.name
          }
        : null,
      inactive: this.inactive,
      last_update: this.last_update,
      homeAccountId: this.homeAccountId,
      localAccountId: this.localAccountId,
      lang: this.lang,
      accessGroups: [...this.accessGroups]
    }
  }
}
